import { useState } from "react";
import { toast } from "sonner";
import { Share2, MessageCircle, Copy, X } from "lucide-react";

/**
 * ShareButton — one-tap Share for any GTF listing.
 * Uses Web Share API if available (mobile native sheet); otherwise shows an inline sheet
 * with WhatsApp, Instagram (open app), and Copy Link fallbacks.
 */
export default function ShareButton({ id, title, price, location, className = "", testid = "listing-share" }) {
  const [openSheet, setOpenSheet] = useState(false);
  const url = `${window.location.origin}/p/${id}`;
  const message =
`🌾 ${title || "Post on GTF"}` +
(price ? `\n💰 ₹${price}` : "") +
(location ? `\n📍 ${location}` : "") +
`\n\nSee it on Grow Together Farmily 👇\n${url}`;

  const shareViaNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || "GTF Post", text: message, url });
        return true;
      } catch (e) {
        if (e?.name === "AbortError") return true; // user cancelled — silent
        console.warn("[GTF] Web Share failed:", e);
      }
    }
    return false;
  };

  const onClick = async () => {
    const done = await shareViaNative();
    if (!done) setOpenSheet(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      setOpenSheet(false);
    } catch (e) {
      toast.error("Could not copy — long press the link to copy");
    }
  };

  const openWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank");
    setOpenSheet(false);
  };

  const openInstagram = async () => {
    // Instagram doesn't accept a text URL; best we can do is copy + open the app
    await copy();
    // On mobile this opens the Instagram app if installed
    window.open("instagram://app", "_self");
    setTimeout(() => setOpenSheet(false), 200);
  };

  return (
    <>
      <button data-testid={testid} type="button" onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-800 hover:bg-gray-50 ${className}`}>
        <Share2 className="h-4 w-4" />Share
      </button>

      {openSheet && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setOpenSheet(false)}>
          <div className="w-full max-w-sm bg-white rounded-3xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-900">Share this post</div>
              <button onClick={() => setOpenSheet(false)} className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <button data-testid="share-whatsapp" onClick={openWhatsApp}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100">
                <MessageCircle className="h-6 w-6 text-emerald-600" />
                <span className="text-xs font-medium text-gray-800">WhatsApp</span>
              </button>
              <button data-testid="share-instagram" onClick={openInstagram}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-pink-50 border border-pink-200 hover:bg-pink-100">
                <div className="h-6 w-6 rounded-md bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600" />
                <span className="text-xs font-medium text-gray-800">Instagram</span>
              </button>
              <button data-testid="share-copy" onClick={copy}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-gray-50 border border-gray-200 hover:bg-gray-100">
                <Copy className="h-6 w-6 text-gray-700" />
                <span className="text-xs font-medium text-gray-800">Copy link</span>
              </button>
            </div>
            <div className="mt-4 text-[11px] text-gray-500 break-all bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
              {url}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
